import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { 
  SportsHockey, 
  Schedule, 
  CheckCircle, 
  FilterList,
  TrendingUp,
  Sports,
  EmojiEvents,
  CalendarToday,
  AccessTime
} from '@mui/icons-material';
import axios from 'axios';
import { apiConfig } from '../config/api';

const Games = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    fetchGames();
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(apiConfig.games.teams);
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(apiConfig.games.withPredictions);
      setGames(response.data);
    } catch (error) {
      setError('Failed to fetch games');
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePredictionSubmit = async () => {
    if (!selectedGame || homeScore === '' || awayScore === '') {
      setError('Please enter both scores');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(apiConfig.predictions.create, {
        gameId: selectedGame._id,
        homeScore: parseInt(homeScore),
        awayScore: parseInt(awayScore),
      });

      setSelectedGame(null);
      setHomeScore('');
      setAwayScore('');
      fetchGames(); // Refresh games with new prediction
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save prediction');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusChip = (game) => {
    if (game.status === 'finished') {
      return <Chip label="Päättynyt" color="success" size="small" icon={<CheckCircle />} />;
    }
    if (game.status === 'live') {
      return <Chip label="Live" color="error" size="small" icon={<Sports />} />;
    }
    if (new Date(game.gameDate) <= new Date()) {
      return <Chip label="Käynnissä" color="warning" size="small" icon={<AccessTime />} />;
    }
    return <Chip label="Tulossa" color="default" size="small" icon={<CalendarToday />} />;
  };

  const getFilteredGames = () => {
    let filtered = games;

    // Tab filter
    const now = new Date();
    if (tabValue === 0) { // Upcoming
      filtered = filtered.filter(game => 
        new Date(game.gameDate) > now && game.status === 'scheduled'
      );
    } else if (tabValue === 1) { // Live
      filtered = filtered.filter(game => 
        game.status === 'live' || 
        (new Date(game.gameDate) <= now && game.status === 'scheduled')
      );
    } else if (tabValue === 2) { // Finished
      filtered = filtered.filter(game => game.status === 'finished');
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(game => game.status === statusFilter);
    }

    // Team filter
    if (teamFilter !== 'all') {
      filtered = filtered.filter(game => 
        game.homeTeam._id === teamFilter || game.awayTeam._id === teamFilter
      );
    }

    return filtered.sort((a, b) => new Date(a.gameDate) - new Date(b.gameDate));
  };

  const getGameStats = () => {
    const total = games.length;
    const upcoming = games.filter(g => new Date(g.gameDate) > new Date() && g.status === 'scheduled').length;
    const live = games.filter(g => g.status === 'live' || (new Date(g.gameDate) <= new Date() && g.status === 'scheduled')).length;
    const finished = games.filter(g => g.status === 'finished').length;
    const withPredictions = games.filter(g => g.prediction).length;

    return { total, upcoming, live, finished, withPredictions };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const filteredGames = getFilteredGames();
  const stats = getGameStats();

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          <SportsHockey sx={{ mr: 2, verticalAlign: 'middle' }} />
          Liiga Ottelut 2025-26
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">Yhteensä</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="primary">{stats.upcoming}</Typography>
                <Typography variant="body2" color="text.secondary">Tulossa</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="warning.main">{stats.live}</Typography>
                <Typography variant="body2" color="text.secondary">Live</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="success.main">{stats.finished}</Typography>
                <Typography variant="body2" color="text.secondary">Päättyneet</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="secondary.main">{stats.withPredictions}</Typography>
                <Typography variant="body2" color="text.secondary">Ennusteet</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            centered
          >
            <Tab label={`Tulevat (${stats.upcoming})`} icon={<CalendarToday />} />
            <Tab label={`Live (${stats.live})`} icon={<Sports />} />
            <Tab label={`Päättyneet (${stats.finished})`} icon={<EmojiEvents />} />
          </Tabs>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FilterList color="action" />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tila</InputLabel>
              <Select
                value={statusFilter}
                label="Tila"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Kaikki</MenuItem>
                <MenuItem value="scheduled">Tulossa</MenuItem>
                <MenuItem value="live">Live</MenuItem>
                <MenuItem value="finished">Päättyneet</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Joukkue</InputLabel>
              <Select
                value={teamFilter}
                label="Joukkue"
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <MenuItem value="all">Kaikki joukkueet</MenuItem>
                {teams.map(team => (
                  <MenuItem key={team._id} value={team._id}>
                    {team.shortName} - {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              Näytetään {filteredGames.length} ottelua
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {filteredGames.map((game) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={game._id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    {getStatusChip(game)}
                    <Typography variant="caption" color="text.secondary">
                      Kierros {game.round}
                    </Typography>
                  </Box>

                  <Box textAlign="center" mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box textAlign="center" flex={1}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: 'primary.main',
                            mx: 'auto',
                            mb: 0.5,
                            fontSize: '0.8rem'
                          }}
                        >
                          {game.homeTeam.shortName}
                        </Avatar>
                        <Typography variant="caption" display="block">
                          {game.homeTeam.shortName}
                        </Typography>
                      </Box>
                      
                      <Box textAlign="center" mx={1}>
                        <Typography variant="h6" color="text.secondary">vs</Typography>
                      </Box>
                      
                      <Box textAlign="center" flex={1}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: 'secondary.main',
                            mx: 'auto',
                            mb: 0.5,
                            fontSize: '0.8rem'
                          }}
                        >
                          {game.awayTeam.shortName}
                        </Avatar>
                        <Typography variant="caption" display="block">
                          {game.awayTeam.shortName}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {game.homeTeam.name} - {game.awayTeam.name}
                    </Typography>
                  </Box>

                  <Box textAlign="center" mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      <Schedule sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      {formatDate(game.gameDate)}
                    </Typography>
                    {game.venue && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {game.venue}
                      </Typography>
                    )}
                  </Box>

                  {game.status === 'finished' && (
                    <Box textAlign="center" mb={2}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="h5" color="primary">
                          {game.homeScore} - {game.awayScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Lopputulos
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {game.status === 'live' && (
                    <Box textAlign="center" mb={2}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: 'error.50' }}>
                        <Typography variant="h6" color="error.main">
                          {game.homeScore || 0} - {game.awayScore || 0}
                        </Typography>
                        <Typography variant="caption" color="error.main">
                          LIVE
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  {game.prediction ? (
                    <Box>
                      <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={1}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          Ennuste tehty
                        </Typography>
                      </Box>
                      <Typography variant="body2" textAlign="center">
                        Ennusteesi: {game.prediction.predictedHomeScore} - {game.prediction.predictedAwayScore}
                      </Typography>
                      {game.status === 'finished' && (
                        <Typography variant="body2" textAlign="center" color="primary" fontWeight="medium">
                          Pisteet: {game.prediction.pointsEarned}
                        </Typography>
                      )}
                    </Box>
                  ) : game.status === 'scheduled' && new Date(game.gameDate) > new Date() ? (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<TrendingUp />}
                      onClick={() => setSelectedGame(game)}
                      sx={{ mt: 1 }}
                    >
                      Tee ennuste
                    </Button>
                  ) : (
                    <Typography variant="body2" textAlign="center" color="text.secondary">
                      Ennustusaika päättynyt
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredGames.length === 0 && (
          <Box textAlign="center" py={8}>
            <SportsHockey sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Ei otteluita valituilla suodattimilla
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kokeile muuttaa suodattimia nähdäksesi otteluita
            </Typography>
          </Box>
        )}
      </Box>

      {/* Prediction Dialog */}
      <Dialog open={!!selectedGame} onClose={() => setSelectedGame(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUp color="primary" />
            Tee ennuste
          </Box>
          {selectedGame && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              {selectedGame.homeTeam.name} vs {selectedGame.awayTeam.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedGame && (
            <Box>
              <Box textAlign="center" mb={3}>
                <Typography variant="body2" color="text.secondary">
                  <Schedule sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {formatDate(selectedGame.gameDate)}
                </Typography>
                {selectedGame.venue && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedGame.venue}
                  </Typography>
                )}
              </Box>
              
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Box flex={1}>
                  <Box textAlign="center" mb={1}>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: 'primary.main',
                        mx: 'auto',
                        mb: 1
                      }}
                    >
                      {selectedGame.homeTeam.shortName}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedGame.homeTeam.name}
                    </Typography>
                  </Box>
                  <TextField
                    label="Maalit"
                    type="number"
                    value={homeScore}
                    onChange={(e) => setHomeScore(e.target.value)}
                    inputProps={{ min: 0, max: 20 }}
                    fullWidth
                    size="small"
                  />
                </Box>
                
                <Typography variant="h4" color="text.secondary" sx={{ mt: 2 }}>
                  -
                </Typography>
                
                <Box flex={1}>
                  <Box textAlign="center" mb={1}>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: 'secondary.main',
                        mx: 'auto',
                        mb: 1
                      }}
                    >
                      {selectedGame.awayTeam.shortName}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedGame.awayTeam.name}
                    </Typography>
                  </Box>
                  <TextField
                    label="Maalit"
                    type="number"
                    value={awayScore}
                    onChange={(e) => setAwayScore(e.target.value)}
                    inputProps={{ min: 0, max: 20 }}
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                Ennuste tulee tehdä ennen ottelun alkua. Pisteitä saa tarkan tuloksen (3p), 
                oikean voittajan (1p) tai oikean maalieron (1p) perusteella.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedGame(null)}>
            Peruuta
          </Button>
          <Button
            onClick={handlePredictionSubmit}
            variant="contained"
            disabled={submitting || homeScore === '' || awayScore === ''}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {submitting ? 'Tallennetaan...' : 'Tallenna ennuste'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Games;
